import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { WheelTimePicker } from './WheelTimePicker';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(Math.round(d.getMinutes() / 5) * 5 % 60).padStart(2, '0')}`;
}

export function CompleteProjectTaskDialog({
  open,
  onOpenChange,
  task,
  job,
  tenantSettings,
  onComplete,
}) {
  const [startTime, setStartTime] = useState(nowHHMM());
  const [finishTime, setFinishTime] = useState(nowHHMM());
  const [workDescription, setWorkDescription] = useState('');
  const [startSmu, setStartSmu] = useState('');
  const [finishSmu, setFinishSmu] = useState('');
  const [startSmu2, setStartSmu2] = useState('');
  const [finishSmu2, setFinishSmu2] = useState('');
  const [saving, setSaving] = useState(false);

  const smuRules = tenantSettings?.field_rules?.smu;
  const smuEnabled = smuRules?.enabled;
  const smuRequired = smuRules?.required_to_complete_job;
  const secondaryEnabled = smuRules?.secondary_meter_enabled;
  const equipment = job?.equipment;
  const showSmu = smuEnabled && equipment;

  const smuLabel = equipment?.smu_primary_label || 'Hours';
  const smuLabel2 = equipment?.smu_secondary_label || 'KM';

  // Pre-fill start SMU from asset's current meter
  useState(() => {
    if (equipment?.hours_kms != null) setStartSmu(String(equipment.hours_kms));
    if (equipment?.hours_kms_2 != null) setStartSmu2(String(equipment.hours_kms_2));
  });

  const smuDelta = useMemo(() => {
    if (startSmu === '' || finishSmu === '') return null;
    return (parseFloat(finishSmu) - parseFloat(startSmu)).toFixed(1);
  }, [startSmu, finishSmu]);

  const smuWarn = smuDelta !== null && parseFloat(finishSmu) < parseFloat(startSmu);

  const handleSubmit = async () => {
    if (!workDescription.trim()) return;
    setSaving(true);
    try {
      await onComplete({
        start_time: startTime,
        finish_time: finishTime,
        work_description: workDescription.trim(),
        start_smu: startSmu !== '' ? parseFloat(startSmu) : null,
        finish_smu: finishSmu !== '' ? parseFloat(finishSmu) : null,
        start_smu_2: startSmu2 !== '' ? parseFloat(startSmu2) : null,
        finish_smu_2: finishSmu2 !== '' ? parseFloat(finishSmu2) : null,
      });
    } finally { setSaving(false); }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="complete-project-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-400" /> Complete Task
          </DialogTitle>
          <p className="text-sm text-[var(--m-text-secondary)] mt-1">{task.description}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Start Time</Label>
              <WheelTimePicker
                value={startTime}
                onChange={setStartTime}
                minuteStep={5}
                className="w-full h-10 rounded-md bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-sm hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                testId="pt-start-time"
              />
            </div>
            <div>
              <Label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Finish Time</Label>
              <WheelTimePicker
                value={finishTime}
                onChange={setFinishTime}
                minuteStep={5}
                className="w-full h-10 rounded-md bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-sm hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                testId="pt-finish-time"
              />
            </div>
          </div>

          {/* SMU Section */}
          {showSmu && (
            <div className="border border-[var(--m-border)] rounded p-3 bg-[var(--m-bg-input)]" data-testid="pt-smu-block">
              <span className="text-[10px] uppercase tracking-wide text-[var(--m-text-secondary)]">
                Meter Readings {smuRequired && <span className="text-amber-400">*</span>}
              </span>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-[10px] text-[var(--m-text-secondary)]">Start ({smuLabel})</Label>
                  <Input
                    type="number" step="0.1" inputMode="decimal"
                    className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                    value={startSmu}
                    onChange={(e) => setStartSmu(e.target.value)}
                    placeholder="e.g. 1245.3"
                    data-testid="pt-smu-start"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[var(--m-text-secondary)]">Finish ({smuLabel})</Label>
                  <Input
                    type="number" step="0.1" inputMode="decimal"
                    className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                    value={finishSmu}
                    onChange={(e) => setFinishSmu(e.target.value)}
                    placeholder="e.g. 1253.7"
                    data-testid="pt-smu-finish"
                  />
                </div>
              </div>
              {secondaryEnabled && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] text-[var(--m-text-secondary)]">Start ({smuLabel2})</Label>
                    <Input
                      type="number" step="0.1" inputMode="decimal"
                      className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                      value={startSmu2}
                      onChange={(e) => setStartSmu2(e.target.value)}
                      data-testid="pt-smu2-start"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[var(--m-text-secondary)]">Finish ({smuLabel2})</Label>
                    <Input
                      type="number" step="0.1" inputMode="decimal"
                      className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                      value={finishSmu2}
                      onChange={(e) => setFinishSmu2(e.target.value)}
                      data-testid="pt-smu2-finish"
                    />
                  </div>
                </div>
              )}
              {smuDelta !== null && (
                <div className="text-[11px] text-[var(--m-text-body)] mt-2">
                  Delta: <span className={smuWarn ? 'text-amber-400' : 'text-[#ffd108] font-medium'}>{smuDelta}</span> {smuLabel}
                  {smuWarn && (
                    <span className="text-amber-400 ml-2 inline-flex items-center gap-0.5">
                      <AlertTriangle className="h-3 w-3" /> finish &lt; start
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Work Description */}
          <div>
            <Label className="text-xs text-[var(--m-text-secondary)] mb-1 block">
              What did you do? <span className="text-red-400">*</span>
            </Label>
            <textarea
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              placeholder="Describe the work completed for this task..."
              rows={3}
              className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md px-3 py-2 text-[var(--m-text)] text-sm focus:outline-none focus:border-[#ffd108]"
              data-testid="pt-work-description"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="border-[var(--m-border-light)] text-[var(--m-text-body)]"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="pt-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-[var(--m-text)]"
            onClick={handleSubmit}
            disabled={saving || !workDescription.trim()}
            data-testid="pt-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Complete Task</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompleteProjectTaskDialog;
